// ===============================================================================
// BusinessGroupDetail.js - Business Group Detail View for SplitMon
//===============================================================================
// Location: /var/www/wow/frontend/src/BusinessGroupDetail.js
// Premium feature - Detailed view for managing business group expenses
// Elegant, smaller fonts suitable for business use
// UPDATED: Added expense detail modal, receipts, multiple payers, settlement date,
//          refresh button, fixed copy invite code, fixed percentage display
// UPDATED: Added Income feature - Group Income as virtual member
// ===============================================================================

import React, { useState, useEffect, useCallback } from "react";
import { businessAPI, formatCurrency, formatDate } from "./api";
import "./BusinessGroupDetail.css";

function BusinessGroupDetail({ groupId, user, onBack, onGroupDeleted }) {
  // ===============================================================================
  // State: Group Data
  // ===============================================================================
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ===============================================================================
  // State: Tabs
  // ===============================================================================
  const [activeTab, setActiveTab] = useState("balances"); // balances, expenses, income, settings

  // ===============================================================================
  // State: Expenses
  // ===============================================================================
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [hasMoreExpenses, setHasMoreExpenses] = useState(true);

  // ===============================================================================
  // State: Expense Detail Modal (NEW)
  // ===============================================================================
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [loadingExpenseDetail, setLoadingExpenseDetail] = useState(false);

  // ===============================================================================
  // State: Receipt (NEW)
  // ===============================================================================
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptInfo, setReceiptInfo] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  // ===============================================================================
  // State: Balances
  // ===============================================================================
  const [balances, setBalances] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [groupIncomeBalance, setGroupIncomeBalance] = useState(0);

  // ===============================================================================
  // State: Add Expense Form
  // ===============================================================================
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseCategory, setExpenseCategory] = useState("");
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [splitType, setSplitType] = useState("percentage");
  const [splitAmounts, setSplitAmounts] = useState({});
  const [memberShares, setMemberShares] = useState({});
  const [addingExpense, setAddingExpense] = useState(false);
  const [expenseError, setExpenseError] = useState("");

  // ===============================================================================
  // State: Multiple Payers (NEW)
  // ===============================================================================
  const [multiplePayers, setMultiplePayers] = useState(false);
  const [payerAmounts, setPayerAmounts] = useState({});

  // ===============================================================================
  // State: Settlement (UPDATED with settlement_date)
  // ===============================================================================
  const [showSettlement, setShowSettlement] = useState(false);
  const [settlementTo, setSettlementTo] = useState("");
  const [settlementAmount, setSettlementAmount] = useState("");
  const [settlementNote, setSettlementNote] = useState("");
  const [settlementDate, setSettlementDate] = useState(new Date().toISOString().split('T')[0]);
  const [addingSettlement, setAddingSettlement] = useState(false);

  // ===============================================================================
  // State: Payment History
  // ===============================================================================
  const [settlements, setSettlements] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  // ===============================================================================
  // State: Split Settings
  // ===============================================================================
  const [editingSplits, setEditingSplits] = useState(false);
  const [tempSplitPercentages, setTempSplitPercentages] = useState({});
  const [savingSplits, setSavingSplits] = useState(false);
  const [editingShares, setEditingShares] = useState(false);
  const [tempDefaultShares, setTempDefaultShares] = useState({});
  const [savingShares, setSavingShares] = useState(false);

  // ===============================================================================
  // State: Custom Category
  // ===============================================================================
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryIcon, setNewCategoryIcon] = useState("📌");
  const [addingCategory, setAddingCategory] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  // ===============================================================================
  // Helper: Check if user is admin
  // ===============================================================================
  const isAdmin = group?.my_role === 'admin';

  // Kick and Leave Group states
  const [kickingMember, setKickingMember] = useState(null);
  const [leavingGroup, setLeavingGroup] = useState(false);

  // ===============================================================================
  // State: Income Feature
  // ===============================================================================
  const [incomeList, setIncomeList] = useState([]);
  const [loadingIncome, setLoadingIncome] = useState(false);
  const [hasMoreIncome, setHasMoreIncome] = useState(true);
  const [incomeSummary, setIncomeSummary] = useState(null);

  // ===============================================================================
  // State: Add Income Form
  // ===============================================================================
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [incomeAmount, setIncomeAmount] = useState("");
  const [incomeDescription, setIncomeDescription] = useState("");
  const [incomeSourceType, setIncomeSourceType] = useState("");
  const [incomeSourceName, setIncomeSourceName] = useState("");
  const [incomeDate, setIncomeDate] = useState(new Date().toISOString().split('T')[0]);
  const [incomeSplitType, setIncomeSplitType] = useState("shares");
  const [incomeSplitAmounts, setIncomeSplitAmounts] = useState({});
  const [incomeMemberShares, setIncomeMemberShares] = useState({});
  const [incomeNotes, setIncomeNotes] = useState("");
  const [addingIncome, setAddingIncome] = useState(false);
  const [incomeError, setIncomeError] = useState("");

  // ===============================================================================
  // State: Income Detail Modal
  // ===============================================================================
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [loadingIncomeDetail, setLoadingIncomeDetail] = useState(false);

  // ===============================================================================
  // State: Income Receipt
  // ===============================================================================
  const [incomeReceiptFile, setIncomeReceiptFile] = useState(null);
  const [uploadingIncomeReceipt, setUploadingIncomeReceipt] = useState(false);
  const [incomeReceiptInfo, setIncomeReceiptInfo] = useState(null);
  const [loadingIncomeReceipt, setLoadingIncomeReceipt] = useState(false);

  // ===============================================================================
  // State: Settle From Income Modal
  // ===============================================================================
  const [showSettleFromIncome, setShowSettleFromIncome] = useState(false);
  const [settleFromIncomeTo, setSettleFromIncomeTo] = useState("");
  const [settleFromIncomeAmount, setSettleFromIncomeAmount] = useState("");
  const [settleFromIncomeNote, setSettleFromIncomeNote] = useState("");
  const [settleFromIncomeDate, setSettleFromIncomeDate] = useState(new Date().toISOString().split('T')[0]);
  const [settlingFromIncome, setSettlingFromIncome] = useState(false);

  // ===============================================================================
  // Income Source Types constant
  // ===============================================================================
  const INCOME_SOURCE_TYPES = [
    { value: "company", label: "🏢 Company Revenue", icon: "🏢" },
    { value: "employer", label: "💼 Employer/Salary", icon: "💼" },
    { value: "government", label: "🏛️ Government Grant", icon: "🏛️" },
    { value: "subsidy", label: "💵 Subsidy", icon: "💵" },
    { value: "rental", label: "🏠 Rental Income", icon: "🏠" },
    { value: "tax_return", label: "📋 Tax Return", icon: "📋" },
    { value: "dividend", label: "📈 Dividend", icon: "📈" },
    { value: "investment", label: "💹 Investment Return", icon: "💹" },
    { value: "refund", label: "↩️ Refund", icon: "↩️" },
    { value: "other", label: "📦 Other", icon: "📦" },
  ];

  // ===============================================================================
  // Load Group Data
  // ===============================================================================
  const loadGroupData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await businessAPI.getGroup(groupId);
      if (res.data) {
        setGroup(res.data);
        // Initialize split percentages and shares from actual values (FIX for 0% issue)
        const splits = {};
        const shares = {};
        const payers = {};
        res.data.members?.forEach(m => {
          // Use actual value, including 0, not a fallback
          splits[m.user_id] = m.split_percentage !== null && m.split_percentage !== undefined 
            ? m.split_percentage 
            : null;
          shares[m.user_id] = m.default_shares || 1;
          payers[m.user_id] = 0;
        });
        setTempSplitPercentages(splits);
        setTempDefaultShares(shares);
        setPayerAmounts(payers);
        // Initialize member shares for expense form Bassam
        const mShares = {};
        res.data.members?.forEach(m => {
          mShares[m.user_id] = m.default_shares || 1;
        });
        setMemberShares(mShares);
        // Initialize income member shares
        setIncomeMemberShares(mShares);
      }
    } catch (err) {
      console.error("Load group error:", err);
      setError("Failed to load business group");
    }
    setLoading(false);
  }, [groupId]);

  // ===============================================================================
  // Load Balances with Income Support
  // ===============================================================================
  const loadBalances = useCallback(async () => {
    setLoadingBalances(true);
    try {
      // Try to load balances with income first
      const res = await businessAPI.getBalancesWithIncome(groupId);
      if (res.data?.ok) {
        setBalances(res.data.balances || []);
        setDebts(res.data.debts || []);
        setGroupIncomeBalance(res.data.group_income_balance || 0);
      }
    } catch (err) {
      // Fallback to regular balances if income endpoint not available
      console.error("Load balances with income error:", err);
      try {
        const fallbackRes = await businessAPI.getBalances(groupId);
        if (fallbackRes.data?.ok) {
          setBalances(fallbackRes.data.balances || []);
          setDebts(fallbackRes.data.debts || []);
          setGroupIncomeBalance(0);
        }
      } catch (fallbackErr) {
        console.error("Load balances error:", fallbackErr);
      }
    }
    setLoadingBalances(false);
  }, [groupId]);

  const loadExpenses = useCallback(async (reset = false) => {
    if (loadingExpenses) return;
    setLoadingExpenses(true);
    try {
      const offset = reset ? 0 : expenses.length;
      const res = await businessAPI.getExpenses(groupId, 20, offset);
      if (res.data?.ok) {
        const newExpenses = res.data.expenses || [];
        if (reset) {
          setExpenses(newExpenses);
        } else {
          setExpenses(prev => [...prev, ...newExpenses]);
        }
        setHasMoreExpenses(newExpenses.length === 20);
      }
    } catch (err) {
      console.error("Load expenses error:", err);
    }
    setLoadingExpenses(false);
  }, [groupId, expenses.length, loadingExpenses]);

  useEffect(() => {
    loadGroupData();
    loadBalances();
    loadExpenses(true);
  }, [loadGroupData, loadBalances]);

  // ===============================================================================
  // Split Calculation
  // ===============================================================================
  const recalculateSplits = useCallback((amount, type) => {
    if (!group?.members) return;
    const newSplitAmounts = {};
    
    if (type === "equal") {
      const perPerson = Math.round((amount / group.members.length) * 100) / 100;
      let remaining = amount;
      group.members.forEach((m, i) => {
        if (i === group.members.length - 1) {
          newSplitAmounts[m.user_id] = Math.round(remaining * 100) / 100;
        } else {
          newSplitAmounts[m.user_id] = perPerson;
          remaining -= perPerson;
        }
      });
    } else if (type === "percentage") {
      group.members.forEach(m => {
        // Use actual stored percentage, including 0
        const pct = tempSplitPercentages[m.user_id] !== null && tempSplitPercentages[m.user_id] !== undefined
          ? tempSplitPercentages[m.user_id]
          : (100 / group.members.length);
        newSplitAmounts[m.user_id] = Math.round((amount * pct / 100) * 100) / 100;
      });
    } else if (type === "shares") {
      const totalShares = Object.values(memberShares).reduce((sum, s) => sum + (parseInt(s) || 0), 0);
      if (totalShares > 0) {
        const perShare = amount / totalShares;
        group.members.forEach(m => {
          const shares = parseInt(memberShares[m.user_id]) || 1;
          newSplitAmounts[m.user_id] = Math.round((perShare * shares) * 100) / 100;
        });
      }
    }
    setSplitAmounts(newSplitAmounts);
  }, [group?.members, tempSplitPercentages, memberShares]);

  useEffect(() => {
    if (expenseAmount && group?.members) {
      recalculateSplits(parseFloat(expenseAmount) || 0, splitType);
    }
  }, [expenseAmount, splitType, group?.members, recalculateSplits]);

  // ===============================================================================
  // Payer Amount Helpers (NEW)
  // ===============================================================================
  const getPayerAmountsTotal = () => {
    return Object.values(payerAmounts).reduce((sum, a) => sum + (parseFloat(a) || 0), 0);
  };

  // ===============================================================================
  // Load Expense Detail (NEW)
  // ===============================================================================
  const handleViewExpense = async (expense) => {
    setLoadingExpenseDetail(true);
    setSelectedExpense(expense);
    
    try {
      const res = await businessAPI.getExpense(expense.id);
      if (res.data?.ok) {
        setSelectedExpense(res.data.expense);
      }
    } catch (err) {
      console.error("Load expense detail error:", err);
    }
    setLoadingExpenseDetail(false);
  };

  // ===============================================================================
  // Receipt Functions (NEW)
  // ===============================================================================
  const handleReceiptFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Maximum size: 5 MB");
        e.target.value = "";
        return;
      }
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        alert("Invalid file type. Allowed: JPG, PNG, PDF");
        e.target.value = "";
        return;
      }
      setReceiptFile(file);
    }
  };

  const handleUploadReceipt = async (expenseId) => {
    if (!receiptFile) return;
    
    setUploadingReceipt(true);
    try {
      const response = await businessAPI.uploadReceipt(expenseId, receiptFile);
      if (response.data?.ok) {
        alert("Receipt uploaded successfully!");
        setReceiptFile(null);
        loadReceiptInfo(expenseId);
      }
    } catch (err) {
      console.error("Upload receipt error:", err);
      const message = err.response?.data?.detail || "Failed to upload receipt";
      alert(message);
    }
    setUploadingReceipt(false);
  };

  const loadReceiptInfo = async (expenseId) => {
    setLoadingReceipt(true);
    try {
      const response = await businessAPI.getReceiptInfo(expenseId);
      if (response.data?.ok) {
        setReceiptInfo(response.data);
      }
    } catch (err) {
      console.error("Load receipt info error:", err);
      setReceiptInfo(null);
    }
    setLoadingReceipt(false);
  };

  const handleDownloadReceipt = async (expenseId, filename) => {
    try {
      const response = await businessAPI.downloadReceipt(expenseId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Download receipt error:", err);
      alert("Failed to download receipt");
    }
  };

  const handleDeleteReceipt = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this receipt?")) return;
    
    try {
      const response = await businessAPI.deleteReceipt(expenseId);
      if (response.data?.ok) {
        alert("Receipt deleted successfully!");
        setReceiptInfo(null);
      }
    } catch (err) {
      console.error("Delete receipt error:", err);
      const message = err.response?.data?.detail || "Failed to delete receipt";
      alert(message);
    }
  };

  // Load receipt info when expense detail modal opens
  useEffect(() => {
    if (selectedExpense && selectedExpense.id) {
      loadReceiptInfo(selectedExpense.id);
    } else {
      setReceiptInfo(null);
    }
  }, [selectedExpense]);

  // ===============================================================================
  // Add Expense (UPDATED with multiple payers)
  // ===============================================================================
  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseAmount || !expenseDescription) return;
    // Validate expense amount is positive
    const amount = parseFloat(expenseAmount);
    if (isNaN(amount) || amount <= 0) {
      setExpenseError("Expense amount must be greater than 0");
      return;
    }    
    // Validate multiple payers
    if (multiplePayers) {
      const payerTotal = getPayerAmountsTotal();
      const expTotal = parseFloat(expenseAmount);
      if (Math.abs(payerTotal - expTotal) > 0.01) {
        setExpenseError(`Payer amounts (${payerTotal.toFixed(2)}) must equal expense total (${expTotal.toFixed(2)})`);
        return;
      }
    }
    
        // Validate manual split
    if (splitType === 'manual') {
      const splitTotal = Object.values(splitAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      const expTotal = parseFloat(expenseAmount);
      if (Math.abs(splitTotal - expTotal) > 0.01) {
        setExpenseError(`Split total (${splitTotal.toFixed(2)}) must equal expense amount (${expTotal.toFixed(2)})`);
        return;
      }
    }

    setAddingExpense(true);
    setExpenseError("");
    
    try {
      const totalAmount = parseFloat(expenseAmount);
      const splits = group.members.map(m => {
        const splitAmount = splitAmounts[m.user_id] || 0;
        const splitPct = totalAmount > 0 ? (splitAmount / totalAmount * 100) : 0;
        return {
          user_id: m.user_id,
          amount: splitAmount,
          shares: memberShares[m.user_id] || 1,
          split_percentage: Math.round(splitPct * 100) / 100
        };
      });
      
      const expenseData = {
        amount: totalAmount,
        description: expenseDescription.trim(),
        category_id: expenseCategory && !expenseCategory.startsWith('custom_') ? parseInt(expenseCategory) : null,
        custom_category_id: expenseCategory?.startsWith('custom_') ? parseInt(expenseCategory.replace('custom_', '')) : null,
        expense_date: expenseDate,
        split_type: splitType,
        splits,
        multiple_payers: multiplePayers,
        payers: multiplePayers ? group.members
          .filter(m => parseFloat(payerAmounts[m.user_id]) > 0)
          .map(m => ({
            user_id: m.user_id,
            amount: parseFloat(payerAmounts[m.user_id])
          })) : null
      };
      
      const res = await businessAPI.createExpense(groupId, expenseData);
      
      if (res.data?.ok) {
        setShowAddExpense(false);
        setExpenseAmount("");
        setExpenseDescription("");
        setExpenseCategory("");
        setSplitType("equal");
        setMultiplePayers(false);
        // Reset payer amounts
        const resetPayers = {};
        group.members?.forEach(m => { resetPayers[m.user_id] = 0; });
        setPayerAmounts(resetPayers);
        loadExpenses(true);
        loadBalances();
      }
    } catch (err) {
      console.error("Add expense error:", err);
      setExpenseError(err?.response?.data?.detail || "Failed to add expense");
    }
    setAddingExpense(false);
  };

  // ===============================================================================
  // Add Settlement (UPDATED with settlement_date)
  // ===============================================================================
  const handleAddSettlement = async (e) => {
    e.preventDefault();
    if (!settlementTo || !settlementAmount) return;
    
    setAddingSettlement(true);
    try {
      const res = await businessAPI.createSettlement(groupId, {
        to_user: parseInt(settlementTo),
        amount: parseFloat(settlementAmount),
        note: settlementNote.trim(),
        settlement_date: settlementDate
      });
      
      if (res.data?.ok) {
        setShowSettlement(false);
        setSettlementTo("");
        setSettlementAmount("");
        setSettlementNote("");
        setSettlementDate(new Date().toISOString().split('T')[0]);
        loadBalances();
        if (showPaymentHistory) {
          loadPaymentHistory();
        }
      }
    } catch (err) {
      console.error("Add settlement error:", err);
    }
    setAddingSettlement(false);
  };

  // ===============================================================================
  // Load Payment History
  // ===============================================================================
  const loadPaymentHistory = async () => {
    setLoadingSettlements(true);
    try {
      const res = await businessAPI.getSettlements(groupId, 50, 0);
      if (res.data?.ok) {
        setSettlements(res.data.settlements || []);
      }
    } catch (err) {
      console.error("Load payment history error:", err);
    }
    setLoadingSettlements(false);
    setShowPaymentHistory(true);
  };

  // ===============================================================================
  // Save Split Percentages
  // ===============================================================================
  const handleSaveSplitPercentages = async () => {
    // Validate percentages sum to 100
    const total = Object.values(tempSplitPercentages).reduce((sum, p) => {
      const val = parseFloat(p);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    if (Math.abs(total - 100) > 0.01) {
      alert(`Percentages must sum to 100% (currently ${total.toFixed(1)}%)`);
      return;
    }
    
    setSavingSplits(true);
    try {
      const percentages = Object.entries(tempSplitPercentages).map(([user_id, percentage]) => ({
        user_id: parseInt(user_id),
        percentage: parseFloat(percentage) || 0
      }));
      
      await businessAPI.updateSplitPercentages(groupId, percentages);
      setEditingSplits(false);
      loadGroupData();
    } catch (err) {
      console.error("Save split percentages error:", err);
      alert("Failed to save split percentages");
    }
    setSavingSplits(false);
  };

  // ===============================================================================
  // Save Default Shares
  // ===============================================================================
  const handleSaveDefaultShares = async () => {
    setSavingShares(true);
    try {
      const shares = Object.entries(tempDefaultShares).map(([user_id, shares]) => ({
        user_id: parseInt(user_id),
        shares: parseInt(shares) || 1
      }));
      
      await businessAPI.updateDefaultShares(groupId, shares);
      setEditingShares(false);
      loadGroupData();
    } catch (err) {
      console.error("Save default shares error:", err);
      alert("Failed to save default shares");
    }
    setSavingShares(false);
  };

  // ===============================================================================
  // Add Custom Category
  // ===============================================================================
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    
    setAddingCategory(true);
    try {
      const res = await businessAPI.createCustomCategory(groupId, {
        category_code: newCategoryName.toLowerCase().replace(/\s+/g, '_'),
        category_name: newCategoryName.trim(),
        icon: newCategoryIcon
      });
      
      if (res.data?.id) {
        setShowAddCategory(false);
        setNewCategoryName("");
        setNewCategoryIcon("📌");
        loadGroupData();
      }
    } catch (err) {
      console.error("Add category error:", err);
    }
    setAddingCategory(false);
  };

  // ===============================================================================
  // Delete Custom Category
  // ===============================================================================
  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete the "${categoryName}" category? This cannot be undone.`)) return;

    setDeletingCategory(categoryId);
    try {
      const res = await businessAPI.deleteCustomCategory(groupId, categoryId);
      if (res.data?.ok !== false) {
        loadGroupData();
      }
    } catch (err) {
      console.error("Delete category error:", err);
      alert(err?.response?.data?.detail || "Failed to delete category");
    }
    setDeletingCategory(null);
  };

  // ===============================================================================
  // Delete Expense (UPDATED - permission check happens on backend)
  // ===============================================================================
  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await businessAPI.deleteExpense(expenseId);
      loadExpenses(true);
      loadBalances();
      setSelectedExpense(null);
    } catch (err) {
      console.error("Delete expense error:", err);
      alert(err?.response?.data?.detail || "Failed to delete expense");
    }
  };

  // ===============================================================================
  // Delete Group
  // ===============================================================================
  const handleDeleteGroup = async () => {
    if (!window.confirm("Are you sure you want to delete this business group? This cannot be undone.")) return;
    try {
      await businessAPI.deleteGroup(groupId);
      onGroupDeleted();
    } catch (err) {
      console.error("Delete group error:", err);
    }
  };

// ===============================================================================
// Kick Member (Admin only) - NEW
// ===============================================================================
const handleKickMember = async (memberId, memberName) => {
  if (!window.confirm(`Are you sure you want to remove ${memberName} from this group? This action cannot be undone.`)) {
    return;
  }
  
  setKickingMember(memberId);
  try {
    const res = await businessAPI.kickMember(groupId, memberId);
    if (res.data?.ok) {
      alert(`${memberName} has been removed from the group.`);
      loadGroupData(); // Reload to update members list
      loadBalances();  // Reload balances
    }
  } catch (err) {
    console.error("Kick member error:", err);
    const message = err?.response?.data?.detail || "Failed to remove member";
    alert(message);
  }
  setKickingMember(null);
};

  // ===============================================================================
  // Leave Group (for non-admin members)
  // ===============================================================================
  const handleLeaveGroup = async () => {
    if (!window.confirm("Are you sure you want to leave this group? This action cannot be undone.")) {
      return;
    }
    
    setLeavingGroup(true);
    try {
      const res = await businessAPI.leaveGroup(groupId);
      if (res.data?.ok) {
        alert("You have left the group.");
        onGroupDeleted();
      }
    } catch (err) {
      console.error("Leave group error:", err);
      const message = err?.response?.data?.detail || "Failed to leave group";
      alert(message);
    }
    setLeavingGroup(false);
  };


  // ===============================================================================
  // Export Functions
  // ===============================================================================
  const handleExportExpenses = async () => {
    try {
      const response = await businessAPI.exportExpensesCsv(groupId);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${group.name}_expenses.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export expenses error:", err);
      alert("Failed to export expenses");
    }
  };

  const handleExportSettlements = async () => {
    try {
      const response = await businessAPI.exportSettlementsCsv(groupId);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${group.name}_settlements.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export settlements error:", err);
      alert("Failed to export settlements");
    }
  };

const handleExportIncome = async () => {
    try {
      const response = await businessAPI.exportIncomeCsv(groupId);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${group.name}_income.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export income error:", err);
      alert("Failed to export income");
    }
  };

  // ===============================================================================
  // Copy Invite Code (FIXED - with fallback for non-HTTPS)
  // ===============================================================================
  const copyInviteCode = () => {
    const code = group?.invite_code || "";
    
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code)
        .then(() => alert("Invite code copied!"))
        .catch(() => fallbackCopyText(code));
    } else {
      // Fallback for non-HTTPS or older browsers
      fallbackCopyText(code);
    }
  };

  const fallbackCopyText = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      alert("Invite code copied!");
    } catch (err) {
      alert("Copy failed. Code: " + text);
    }
    document.body.removeChild(textArea);
  };

  // ===============================================================================
  // Helper: Get display percentage (FIXED - shows actual value including 0)
  // ===============================================================================
  const getDisplayPercentage = (userId) => {
    const val = tempSplitPercentages[userId];
    // If we have a stored value (including 0), use it
    if (val !== null && val !== undefined) {
      return parseFloat(val).toFixed(1);
    }
    // Otherwise default to equal split
    return (100 / (group?.members?.length || 1)).toFixed(1);
  };

  // ===============================================================================
  // INCOME FUNCTIONS
  // ===============================================================================

  // Load Income List
  const loadIncome = useCallback(async (reset = false) => {
    if (loadingIncome) return;
    setLoadingIncome(true);
    try {
      const offset = reset ? 0 : incomeList.length;
      const res = await businessAPI.getIncome(groupId, 20, offset);
      if (res.data?.ok) {
        const newIncome = res.data.income || [];
        if (reset) {
          setIncomeList(newIncome);
        } else {
          setIncomeList(prev => [...prev, ...newIncome]);
        }
        setHasMoreIncome(newIncome.length === 20);
      }
    } catch (err) {
      console.error("Load income error:", err);
    }
    setLoadingIncome(false);
  }, [groupId, incomeList.length, loadingIncome]);

  // Load Income Summary
  const loadIncomeSummary = useCallback(async () => {
    try {
      const res = await businessAPI.getIncomeSummary(groupId);
      if (res.data?.ok) {
        setIncomeSummary(res.data);
        setGroupIncomeBalance(res.data.group_income_balance || 0);
      }
    } catch (err) {
      console.error("Load income summary error:", err);
    }
  }, [groupId]);

  // Income Split Calculation
  const recalculateIncomeSplits = useCallback((amount, type) => {
    if (!group?.members) return;
    const newSplitAmounts = {};
    
    if (type === "equal") {
      const perPerson = Math.round((amount / group.members.length) * 100) / 100;
      let remaining = amount;
      group.members.forEach((m, i) => {
        if (i === group.members.length - 1) {
          newSplitAmounts[m.user_id] = Math.round(remaining * 100) / 100;
        } else {
          newSplitAmounts[m.user_id] = perPerson;
          remaining -= perPerson;
        }
      });
    } else if (type === "percentage") {
      group.members.forEach(m => {
        const pct = tempSplitPercentages[m.user_id] !== null && tempSplitPercentages[m.user_id] !== undefined
          ? tempSplitPercentages[m.user_id]
          : (100 / group.members.length);
        newSplitAmounts[m.user_id] = Math.round((amount * pct / 100) * 100) / 100;
      });
    } else if (type === "shares") {
      const totalShares = Object.values(incomeMemberShares).reduce((sum, s) => sum + (parseInt(s) || 0), 0);
      if (totalShares > 0) {
        const perShare = amount / totalShares;
        group.members.forEach(m => {
          const shares = parseInt(incomeMemberShares[m.user_id]) || 1;
          newSplitAmounts[m.user_id] = Math.round((perShare * shares) * 100) / 100;
        });
      }
    }
    setIncomeSplitAmounts(newSplitAmounts);
  }, [group?.members, tempSplitPercentages, incomeMemberShares]);

  useEffect(() => {
    if (incomeAmount && group?.members) {
      recalculateIncomeSplits(parseFloat(incomeAmount) || 0, incomeSplitType);
    }
  }, [incomeAmount, incomeSplitType, group?.members, recalculateIncomeSplits]);

  // Add Income Handler
  const handleAddIncome = async (e) => {
    e.preventDefault();
    if (!incomeAmount || !incomeDescription) return;
    
    const amount = parseFloat(incomeAmount);
    if (isNaN(amount) || amount === 0) {
      setIncomeError("Amount cannot be zero");
      return;
    }
    
    setAddingIncome(true);
    setIncomeError("");
    
    try {
      const totalAmount = parseFloat(incomeAmount);
      const splits = group.members.map(m => {
        const splitAmount = incomeSplitAmounts[m.user_id] || 0;
        const splitPct = totalAmount !== 0 ? (splitAmount / Math.abs(totalAmount) * 100) : 0;
        return {
          user_id: m.user_id,
          amount: splitAmount,
          shares: incomeMemberShares[m.user_id] || 1,
          split_percentage: Math.round(splitPct * 100) / 100
        };
      });
      
      const incomeData = {
        amount: totalAmount,
        description: incomeDescription.trim(),
        source_type: incomeSourceType || null,
        source_name: incomeSourceName.trim() || null,
        income_date: incomeDate,
        split_type: incomeSplitType,
        splits,
        notes: incomeNotes.trim() || null
      };
      
      const res = await businessAPI.createIncome(groupId, incomeData);
      
      if (res.data?.ok) {
        setShowAddIncome(false);
        // Reset form
        setIncomeAmount("");
        setIncomeDescription("");
        setIncomeSourceType("");
        setIncomeSourceName("");
        setIncomeDate(new Date().toISOString().split('T')[0]);
        setIncomeSplitType("equal");
        setIncomeNotes("");
        // Reload data
        loadIncome(true);
        loadIncomeSummary();
        loadBalances();
      }
    } catch (err) {
      console.error("Add income error:", err);
      setIncomeError(err?.response?.data?.detail || "Failed to add income");
    }
    setAddingIncome(false);
  };

  // View Income Detail
  const handleViewIncome = async (income) => {
    setLoadingIncomeDetail(true);
    setSelectedIncome(income);
    
    try {
      const res = await businessAPI.getIncomeDetail(income.id);
      if (res.data?.ok) {
        setSelectedIncome(res.data.income);
      }
    } catch (err) {
      console.error("Load income detail error:", err);
    }
    setLoadingIncomeDetail(false);
  };

  // Income Receipt Functions
  const handleIncomeReceiptFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Maximum size: 5 MB");
        e.target.value = "";
        return;
      }
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        alert("Invalid file type. Allowed: JPG, PNG, PDF");
        e.target.value = "";
        return;
      }
      setIncomeReceiptFile(file);
    }
  };

  const handleUploadIncomeReceipt = async (incomeId) => {
    if (!incomeReceiptFile) return;
    
    setUploadingIncomeReceipt(true);
    try {
      const response = await businessAPI.uploadIncomeReceipt(incomeId, incomeReceiptFile);
      if (response.data?.ok) {
        alert("Receipt uploaded successfully!");
        setIncomeReceiptFile(null);
        loadIncomeReceiptInfo(incomeId);
      }
    } catch (err) {
      console.error("Upload income receipt error:", err);
      const message = err.response?.data?.detail || "Failed to upload receipt";
      alert(message);
    }
    setUploadingIncomeReceipt(false);
  };

  const loadIncomeReceiptInfo = async (incomeId) => {
    setLoadingIncomeReceipt(true);
    try {
      const response = await businessAPI.getIncomeReceiptInfo(incomeId);
      if (response.data?.ok) {
        setIncomeReceiptInfo(response.data);
      }
    } catch (err) {
      console.error("Load income receipt info error:", err);
      setIncomeReceiptInfo(null);
    }
    setLoadingIncomeReceipt(false);
  };

  const handleDownloadIncomeReceipt = async (incomeId, filename) => {
    try {
      const response = await businessAPI.downloadIncomeReceipt(incomeId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Download income receipt error:", err);
      alert("Failed to download receipt");
    }
  };

  const handleDeleteIncomeReceipt = async (incomeId) => {
    if (!window.confirm("Are you sure you want to delete this receipt?")) return;
    
    try {
      const response = await businessAPI.deleteIncomeReceipt(incomeId);
      if (response.data?.ok) {
        alert("Receipt deleted successfully!");
        setIncomeReceiptInfo(null);
      }
    } catch (err) {
      console.error("Delete income receipt error:", err);
      const message = err.response?.data?.detail || "Failed to delete receipt";
      alert(message);
    }
  };

  // Load receipt info when income detail modal opens
  useEffect(() => {
    if (selectedIncome && selectedIncome.id) {
      loadIncomeReceiptInfo(selectedIncome.id);
    } else {
      setIncomeReceiptInfo(null);
    }
  }, [selectedIncome]);

  // Settle From Group Income Handler
  const handleSettleFromIncome = async (e) => {
    e.preventDefault();
    if (!settleFromIncomeTo || !settleFromIncomeAmount) return;
    
    setSettlingFromIncome(true);
    try {
      const res = await businessAPI.createSettlementFromIncome(groupId, {
        to_user: parseInt(settleFromIncomeTo),
        amount: parseFloat(settleFromIncomeAmount),
        note: settleFromIncomeNote.trim() || null,
        settlement_date: settleFromIncomeDate
      });
      
      if (res.data) {
        setShowSettleFromIncome(false);
        setSettleFromIncomeTo("");
        setSettleFromIncomeAmount("");
        setSettleFromIncomeNote("");
        setSettleFromIncomeDate(new Date().toISOString().split('T')[0]);
        loadBalances();
        loadIncomeSummary();
        if (showPaymentHistory) {
          loadPaymentHistory();
        }
      }
    } catch (err) {
      console.error("Settle from income error:", err);
      alert(err?.response?.data?.detail || "Failed to record settlement");
    }
    setSettlingFromIncome(false);
  };

  // ===============================================================================
  // Loading State
  // ===============================================================================
  if (loading) {
    return (
      <div className="bgd-loading">
        <div className="bgd-spinner"></div>
        <p>Loading business group...</p>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="bgd-error-page">
        <p>{error || "Group not found"}</p>
        <button onClick={onBack}>← Back to Groups</button>
      </div>
    );
  }

  // ===============================================================================
  // Render
  // ===============================================================================
  return (
    <div className="business-group-detail">
      {/* Header */}
      <div className="bgd-header">
        <div className="bgd-header-info">
          <div className="bgd-type-badge">
            <span>{group.type_icon}</span>
            <span>{group.type_name}</span>
          </div>
          <h1>{group.name}</h1>
          {group.description && <p className="bgd-desc">{group.description}</p>}
        </div>
        <div className="bgd-header-actions">
          <span className="bgd-currency-badge">{group.currency}</span>
          <span className="bgd-member-count">👥 {group.members?.length || 0}</span>
        </div>
      </div>

      {/* Tabs - UPDATED with Income tab */}
      <div className="bgd-tabs">
        <button 
          className={`bgd-tab ${activeTab === 'balances' ? 'active' : ''}`}
          onClick={() => setActiveTab('balances')}
        >
          Balances
        </button>
        <button 
          className={`bgd-tab ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => { setActiveTab('expenses'); if (expenses.length === 0) loadExpenses(true); }}
        >
          Expenses
        </button>
        <button 
          className={`bgd-tab ${activeTab === 'income' ? 'active' : ''}`}
          onClick={() => { setActiveTab('income'); if (incomeList.length === 0) { loadIncome(true); loadIncomeSummary(); } }}
        >
          💰 Income
        </button>
        <button 
          className={`bgd-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="bgd-content">
        {/* ================= BALANCES TAB ================= */}
        {activeTab === 'balances' && (
          <div className="bgd-balances-tab">
            {/* Quick Actions - UPDATED with Refresh button */}
            <div className="bgd-quick-actions">
              <button className="bgd-action-btn primary" onClick={() => setShowAddExpense(true)}>
                + Add Expense
              </button>
              <button className="bgd-action-btn" onClick={() => setShowSettlement(true)}>
                💰 Settle Up
              </button>
              <button className="bgd-action-btn" onClick={loadBalances} disabled={loadingBalances}>
                🔄 {loadingBalances ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {/* Group Income Balance Display */}
            {groupIncomeBalance < -0.01 && (
              <div className="bgd-section bgd-group-income-section">
                <h3>💰 Group Income</h3>
                <div className="bgd-group-income-display">
                  <span className="group-income-label">Undistributed Income:</span>
                  <span className="group-income-amount owes">
                    {formatCurrency(Math.abs(groupIncomeBalance), group.currency)} to distribute
                  </span>
                  {isAdmin && (
                    <button 
                      className="bgd-action-btn success small"
                      onClick={() => setShowSettleFromIncome(true)}
                    >
                      💸 Distribute
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Member Balances */}
            <div className="bgd-section">
              <h3>Member Balances</h3>
              {loadingBalances ? (
                <div className="bgd-mini-loading">Loading...</div>
              ) : (
                <div className="bgd-balances-list">
                  {balances.map(b => (
                    <div key={b.user_id} className="bgd-balance-row">
                      <div className="balance-user">
                        <span className="balance-avatar">
                          {b.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                        <span className="balance-name">{b.name}</span>
                      </div>
                      <div className={`balance-amount ${b.net_balance >= 0 ? 'positive' : 'negative'}`}>
                        {b.net_balance >= 0 ? '+' : ''}{formatCurrency(b.net_balance, group.currency)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Suggested Settlements - UPDATED with Group Income support */}
            {debts.length > 0 && (
              <div className="bgd-section">
                <h3>Suggested Settlements</h3>
                <div className="bgd-debts-list">
                  {debts.map((d, i) => (
                    <div key={i} className={`bgd-debt-row ${d.is_from_income ? 'from-income' : ''}`}>
                      <span className={`debt-from ${d.from_user_id === null ? 'group-income' : ''}`}>
                        {d.from_user_name}
                      </span>
                      <span className="debt-arrow">→</span>
                      <span className="debt-to">{d.to_user_name}</span>
                      <span className="debt-amount">{formatCurrency(d.amount, group.currency)}</span>
                      {d.is_from_income ? (
                        // Settlement from Group Income - Admin only
                        isAdmin ? (
                          <button 
                            className="debt-settle-btn income-settle"
                            onClick={() => {
                              setSettleFromIncomeTo(d.to_user_id.toString());
                              setSettleFromIncomeAmount(d.amount.toString());
                              setShowSettleFromIncome(true);
                            }}
                          >
                            Distribute
                          </button>
                        ) : (
                          <span className="debt-admin-note">Admin only</span>
                        )
                      ) : (
                        // Regular settlement between members
                        <button 
                          className="debt-settle-btn"
                          onClick={() => {
                            setSettlementTo(d.to_user_id.toString());
                            setSettlementAmount(d.amount.toString());
                            setShowSettlement(true);
                          }}
                        >
                          Settle
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment History */}
            <div className="bgd-section">
              {!showPaymentHistory ? (
                <button 
                  className="bgd-load-history-btn"
                  onClick={loadPaymentHistory}
                  disabled={loadingSettlements}
                >
                  📜 {loadingSettlements ? 'Loading...' : 'Load Payment History'}
                </button>
              ) : (
                <>
                  <h3>Payment History</h3>
                  {settlements.length === 0 ? (
                    <p className="bgd-no-history">No settlements recorded yet</p>
                  ) : (
                    <div className="bgd-settlements-list">
                      {settlements.map(s => (
                        <div key={s.id} className={`bgd-settlement-row ${s.from_user_id === null ? 'from-income' : ''}`}>
                          <div className="settlement-info">
                            <span className={`settlement-from ${s.from_user_id === null ? 'group-income' : ''}`}>
                              {s.from_user_name || 'Group Income'}
                            </span>
                            <span className="settlement-arrow">paid</span>
                            <span className="settlement-to">{s.to_user_name}</span>
                          </div>
                          <div className="settlement-details">
                            <span className="settlement-amount">{formatCurrency(s.amount, s.currency)}</span>
                            <span className="settlement-date">{formatDate(s.settlement_date || s.created_at)}</span>
                          </div>
                          {s.note && <div className="settlement-note">"{s.note}"</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ================= EXPENSES TAB ================= */}
        {activeTab === 'expenses' && (
          <div className="bgd-expenses-tab">
            <div className="bgd-expenses-header">
              <button className="bgd-action-btn primary" onClick={() => setShowAddExpense(true)}>
                + Add Expense
              </button>
              <button className="bgd-action-btn primary" onClick={handleExportExpenses}>
                📥 Export Expenses
              </button>
              <button className="bgd-action-btn primary" onClick={handleExportSettlements}>
                📥 Export Payments
              </button>
              <button className="bgd-action-btn primary small" onClick={() => setShowAddCategory(true)}>
                + Custom Category
              </button>
            </div>

            {loadingExpenses && expenses.length === 0 ? (
              <div className="bgd-mini-loading">Loading expenses...</div>
            ) : expenses.length === 0 ? (
              <div className="bgd-empty">
                <p>No expenses yet</p>
                <button className="bgd-action-btn primary" onClick={() => setShowAddExpense(true)}>
                  Add First Expense
                </button>
              </div>
            ) : (
              <div className="bgd-expenses-list">
                {expenses.map(exp => (
                  <div 
                    key={exp.id} 
                    className="bgd-expense-row clickable"
                    onClick={() => handleViewExpense(exp)}
                  >
                    <div className="expense-icon">{exp.category_icon || '📄'}</div>
                    <div className="expense-info">
                      <div className="expense-desc">{exp.description}</div>
                      <div className="expense-meta">
                        <span>{exp.category_name || 'Uncategorized'}</span>
                        <span>•</span>
                        <span>{formatDate(exp.expense_date)}</span>
                        {exp.paid_by_name && (
                          <>
                            <span>•</span>
                            <span>Paid by {exp.paid_by_name}</span>
                          </>
                        )}
                        {exp.multiple_payers && (
                          <>
                            <span>•</span>
                            <span>Multiple payers</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="expense-right">
                      {exp.has_receipt && <span className="receipt-indicator" title="Has receipt">🧾</span>}
                      <span className="expense-amount">
                        {formatCurrency(exp.amount, exp.currency)}
                      </span>
                    </div>
                    {/* Delete button - only for owner or admin */}
                    {(exp.created_by === user?.id || isAdmin) && (
                      <button 
                        className="expense-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteExpense(exp.id);
                        }}
                        title="Delete expense"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                
                {hasMoreExpenses && (
                  <button 
                    className="bgd-load-more"
                    onClick={() => loadExpenses(false)}
                    disabled={loadingExpenses}
                  >
                    {loadingExpenses ? 'Loading...' : 'Load More'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ================= INCOME TAB ================= */}
        {activeTab === 'income' && (
          <div className="bgd-income-tab">
            {/* Income Summary Card */}
            {incomeSummary && (
              <div className="bgd-income-summary-card">
                <div className="income-summary-item">
                  <span className="income-summary-label">Total Income</span>
                  <span className="income-summary-value positive">
                    {formatCurrency(incomeSummary.total_income || 0, group.currency)}
                  </span>
                </div>
                <div className="income-summary-item">
                  <span className="income-summary-label">Group Income Balance</span>
                  <span className={`income-summary-value ${groupIncomeBalance < 0 ? 'negative' : 'neutral'}`}>
                    {formatCurrency(groupIncomeBalance, group.currency)}
                  </span>
                  {groupIncomeBalance < -0.01 && (
                    <span className="income-summary-hint">
                      (owes {formatCurrency(Math.abs(groupIncomeBalance), group.currency)} to members)
                    </span>
                  )}
                </div>
                <div className="income-summary-item">
                  <span className="income-summary-label">Income Records</span>
                  <span className="income-summary-value">{incomeSummary.income_count || 0}</span>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bgd-quick-actions">
              <button className="bgd-action-btn primary" onClick={() => setShowAddIncome(true)}>
                + Add Income
              </button>
              {isAdmin && groupIncomeBalance < -0.01 && (
                <button className="bgd-action-btn success" onClick={() => setShowSettleFromIncome(true)}>
                  💸 Distribute Income
                </button>
              )}
              <button className="bgd-action-btn primary" onClick={handleExportIncome}>
                📥 Export Income
              </button>
              <button 
                className="bgd-action-btn" 
                onClick={() => { loadIncome(true); loadIncomeSummary(); loadBalances(); }}
                disabled={loadingIncome}
              >
                🔄 {loadingIncome ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {/* Important Notice */}
            <div className="bgd-income-notice">
              <strong>📌 Note:</strong> Income records cannot be deleted. To reverse an entry, 
              add a new income with a <strong>negative amount</strong>.
            </div>

            {/* Income List */}
            {loadingIncome && incomeList.length === 0 ? (
              <div className="bgd-mini-loading">Loading income...</div>
            ) : incomeList.length === 0 ? (
              <div className="bgd-empty">
                <p>No income recorded yet</p>
                <button className="bgd-action-btn primary" onClick={() => setShowAddIncome(true)}>
                  Add First Income
                </button>
              </div>
            ) : (
              <div className="bgd-income-list">
                {incomeList.map(inc => (
                  <div 
                    key={inc.id} 
                    className={`bgd-income-row clickable ${inc.amount < 0 ? 'reversal' : ''}`}
                    onClick={() => handleViewIncome(inc)}
                  >
                    <div className="income-icon">
                      {INCOME_SOURCE_TYPES.find(t => t.value === inc.source_type)?.icon || '💰'}
                    </div>
                    <div className="income-info">
                      <div className="income-desc">{inc.description}</div>
                      <div className="income-meta">
                        {inc.source_name && <span>{inc.source_name}</span>}
                        {inc.source_name && <span>•</span>}
                        <span>{formatDate(inc.income_date)}</span>
                        <span>•</span>
                        <span>by {inc.created_by_name}</span>
                        {inc.amount < 0 && (
                          <>
                            <span>•</span>
                            <span className="reversal-badge">REVERSAL</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="income-right">
                      {inc.has_receipt && <span className="receipt-indicator" title="Has receipt">🧾</span>}
                      <span className={`income-amount ${inc.amount >= 0 ? 'positive' : 'negative'}`}>
                        {inc.amount >= 0 ? '+' : ''}{formatCurrency(inc.amount, inc.currency)}
                      </span>
                    </div>
                  </div>
                ))}
                
                {hasMoreIncome && (
                  <button 
                    className="bgd-load-more"
                    onClick={() => loadIncome(false)}
                    disabled={loadingIncome}
                  >
                    {loadingIncome ? 'Loading...' : 'Load More'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ================= SETTINGS TAB ================= */}
        {activeTab === 'settings' && (
          <div className="bgd-settings-tab">
            {/* Invite Code */}
            <div className="bgd-setting-section">
              <h3>Invite Code</h3>
              <div className="bgd-invite-section">
                <span className="bgd-invite-code">{group.invite_code}</span>
                <button className="bgd-copy-btn" onClick={copyInviteCode}>📋 Copy</button>
              </div>
            </div>

            {/* Members */}
            <div className="bgd-setting-section">
              <h3>Members ({group.members?.length || 0})</h3>
              <div className="bgd-members-list">
                {group.members?.map(m => (
                  <div key={m.user_id} className="bgd-member-item">
                    <span className="member-avatar">{m.name?.charAt(0)?.toUpperCase()}</span>
                    <span className="member-info">
                      <span className="member-name">{m.name}</span>
                      <span className="member-role">{m.role}</span>
                    </span>
                    {/* Kick button - Only show for admin, not on self or other admins */}
                    {isAdmin && m.user_id !== user?.id && m.role !== 'admin' && (
                       <button 
                         className="bgd-kick-btn"
                         onClick={() => handleKickMember(m.user_id, m.name)}
                         disabled={kickingMember === m.user_id}
                         title="Remove member from group"
                      >
                        {kickingMember === m.user_id ? '...' : '✕'}
                      </button>
                   )}
                  </div>
                ))}
              </div>
            </div>

            {/* Split Percentages - FIXED display */}
            <div className="bgd-setting-section">
              <div className="bgd-setting-header">
                <h3>Default Split Percentages</h3>
                {!editingSplits ? (
                  <button className="bgd-edit-btn" onClick={() => setEditingSplits(true)}>
                    ✏️ Edit
                  </button>
                ) : (
                  <div className="bgd-edit-actions">
                    <button 
                      className="bgd-save-btn" 
                      onClick={handleSaveSplitPercentages}
                      disabled={savingSplits}
                    >
                      {savingSplits ? 'Saving...' : '✔ Save'}
                    </button>
                    <button 
                      className="bgd-cancel-btn" 
                      onClick={() => {
                        setEditingSplits(false);
                        // Reset to current values from group data
                        const splits = {};
                        group.members?.forEach(m => {
                          splits[m.user_id] = m.split_percentage !== null && m.split_percentage !== undefined
                            ? m.split_percentage
                            : null;
                        });
                        setTempSplitPercentages(splits);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <p className="bgd-setting-desc">Set default split percentages for "By %" expenses</p>
              <div className="bgd-splits-list">
                {group.members?.map(m => (
                  <div key={m.user_id} className="bgd-split-item">
                    <span className="split-name">{m.name}</span>
                    {editingSplits ? (
                      <div className="split-input-wrap">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={tempSplitPercentages[m.user_id] !== null && tempSplitPercentages[m.user_id] !== undefined 
                            ? tempSplitPercentages[m.user_id] 
                            : ''}
                          onChange={(e) => setTempSplitPercentages(prev => ({
                            ...prev,
                            [m.user_id]: e.target.value === '' ? 0 : parseFloat(e.target.value)
                          }))}
                          placeholder={(100 / group.members.length).toFixed(1)}
                        />
                        <span>%</span>
                      </div>
                    ) : (
                      <span className="split-value">
                        {getDisplayPercentage(m.user_id)}%
                      </span>
                    )}
                  </div>
                ))}
                {editingSplits && (
                  <div className="bgd-split-total">
                    Total: {Object.values(tempSplitPercentages).reduce((sum, p) => {
                      const val = parseFloat(p);
                      return sum + (isNaN(val) ? 0 : val);
                    }, 0).toFixed(1)}%
                    {Math.abs(Object.values(tempSplitPercentages).reduce((sum, p) => {
                      const val = parseFloat(p);
                      return sum + (isNaN(val) ? 0 : val);
                    }, 0) - 100) > 0.01 && (
                      <span className="split-warning"> (must be 100%)</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Default Shares */}
            <div className="bgd-setting-section">
              <div className="bgd-setting-header">
                <h3>Default Shares</h3>
                {!editingShares ? (
                  <button className="bgd-edit-btn" onClick={() => setEditingShares(true)}>
                    ✏️ Edit
                  </button>
                ) : (
                  <div className="bgd-edit-actions">
                    <button 
                      className="bgd-save-btn" 
                      onClick={handleSaveDefaultShares}
                      disabled={savingShares}
                    >
                      {savingShares ? 'Saving...' : '✔ Save'}
                    </button>
                    <button 
                      className="bgd-cancel-btn" 
                      onClick={() => {
                        setEditingShares(false);
                        const shares = {};
                        group.members?.forEach(m => {
                          shares[m.user_id] = m.default_shares || 1;
                        });
                        setTempDefaultShares(shares);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <p className="bgd-setting-desc">Set default shares for "By Shares" expenses</p>
              <div className="bgd-splits-list">
                {group.members?.map(m => (
                  <div key={m.user_id} className="bgd-split-item">
                    <span className="split-name">{m.name}</span>
                    {editingShares ? (
                      <div className="split-input-wrap">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={tempDefaultShares[m.user_id] || 1}
                          onChange={(e) => setTempDefaultShares(prev => ({
                            ...prev,
                            [m.user_id]: parseInt(e.target.value) || 0
                          }))}
                        />
                        <span>shares</span>
                      </div>
                    ) : (
                      <span className="split-value">
                        {tempDefaultShares[m.user_id] || 1} {(tempDefaultShares[m.user_id] || 1) === 1 ? 'share' : 'shares'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="bgd-setting-section">
              <h3>Categories</h3>
              <div className="bgd-categories-list">
                {group.categories?.map(c => (
                  <span key={c.id} className={`bgd-category-tag ${c.is_custom ? 'custom' : ''}`}>
                    {c.icon} {c.category_name}
                    {c.is_custom && (
                      <button
                        className="category-delete-btn"
                        onClick={() => handleDeleteCategory(c.id, c.category_name)}
                        disabled={deletingCategory === c.id}
                        title="Delete category"
                      >
                        {deletingCategory === c.id ? '…' : '×'}
                      </button>
                    )}
                  </span>
                ))}
              </div>
              <button className="bgd-action-btn small" onClick={() => setShowAddCategory(true)}>
                + Add Custom Category
              </button>
            </div>

            {/* Danger Zone */}
            <div className="bgd-setting-section danger">
              <h3>Danger Zone</h3>
              
              {/* Delete Group - Admin only */}
              {isAdmin && (
                <button className="bgd-danger-btn" onClick={handleDeleteGroup}>
                  🗑️ Delete Business Group
                </button>
              )}
              
              {/* Leave Group - Non-admin members with 0 balance */}
              {!isAdmin && (
                <>
                  {(() => {
                    const myBalance = balances.find(b => b.user_id === user?.id);
                    const hasZeroBalance = !myBalance || Math.abs(myBalance.net_balance || 0) < 0.01;
                    return hasZeroBalance ? (
                      <button 
                        className="bgd-danger-btn" 
                        onClick={handleLeaveGroup}
                        disabled={leavingGroup}
                      >
                        {leavingGroup ? 'Leaving...' : '🚪 Leave Group'}
                      </button>
                    ) : (
                      <p className="bgd-leave-warning">
                        ⚠️ You must settle your balance before leaving the group.
                      </p>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ================= MODALS ================= */}

      {/* Back Button */}
      <button className="bgd-back-btn" onClick={onBack}>
        ← Back to Groups
      </button>

      {/* ================= ADD EXPENSE MODAL ================= */}
      {showAddExpense && (
        <div className="bgd-modal-overlay" onClick={() => setShowAddExpense(false)}>
          <div className="bgd-modal" onClick={e => e.stopPropagation()}>
            <div className="bgd-modal-header">
              <h2>Add Expense</h2>
              <button className="bgd-modal-close" onClick={() => setShowAddExpense(false)}>×</button>
            </div>
            <form onSubmit={handleAddExpense} className="bgd-modal-form">
              {expenseError && <div className="bgd-error">{expenseError}</div>}
              
              <div className="bgd-form-row">
                <label>Amount ({group.currency}) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={expenseAmount}
                  onChange={e => setExpenseAmount(e.target.value)}
                  required
                />
              </div>
              
              <div className="bgd-form-row">
                <label>Description *</label>
                <input
                  type="text"
                  value={expenseDescription}
                  onChange={e => setExpenseDescription(e.target.value)}
                  required
                />
              </div>
              
              <div className="bgd-form-row">
                <label>Category</label>
                <select value={expenseCategory} onChange={e => setExpenseCategory(e.target.value)}>
                  <option value="">-- Select --</option>
                  {group.categories?.map(c => (
                    <option key={c.is_custom ? `custom_${c.id}` : c.id} 
                            value={c.is_custom ? `custom_${c.id}` : c.id}>
                      {c.icon} {c.category_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bgd-form-row">
                <label>Expense Date</label>
                <input
                  type="date"
                  value={expenseDate}
                  onChange={e => setExpenseDate(e.target.value)}
                />
              </div>

              {/* Multiple Payers Toggle */}
              <div className="bgd-form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={multiplePayers}
                    onChange={e => setMultiplePayers(e.target.checked)}
                  />
                  Multiple payers
                </label>
              </div>

              {/* Payer Amounts (if multiple payers) */}
              {multiplePayers && (
                <div className="bgd-form-row">
                  <label>Who Paid? (Total: {getPayerAmountsTotal().toFixed(2)})</label>
                  <div className="bgd-payers-list">
                    {group.members?.map(m => (
                      <div key={m.user_id} className="bgd-payer-row">
                        <span>{m.name}</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={payerAmounts[m.user_id] || 0}
                          onChange={e => setPayerAmounts(prev => ({
                            ...prev,
                            [m.user_id]: e.target.value
                          }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="bgd-form-row">
                <label>Split Type</label>
                <select value={splitType} onChange={e => setSplitType(e.target.value)}>
                  <option value="equal">Equal Split</option>
                  <option value="percentage">By Percentage</option>
                  <option value="shares">By Shares</option>
                  <option value="manual">Manual Entry</option>
                </select>
              </div>
              
              {/* Split Preview */}
              {/* Split Preview */}
<div className="bgd-form-row">
  <label>Split Preview {splitType === 'manual' && `(Total: ${formatCurrency(Object.values(splitAmounts).reduce((a, b) => a + (parseFloat(b) || 0), 0), group.currency)})`}</label>
  <div className="bgd-split-preview">
    {group.members?.map(m => (
      <div key={m.user_id} className="split-preview-row">
        <span>{m.name}</span>
        {splitType === 'shares' && (
          <input
            type="number"
            min="0"
            value={memberShares[m.user_id] || 1}
            onChange={e => setMemberShares(prev => ({
              ...prev,
              [m.user_id]: parseInt(e.target.value) || 0
            }))}
            style={{width: '50px', marginRight: '10px'}}
          />
        )}
        {splitType === 'manual' ? (
          <input
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={splitAmounts[m.user_id] || ''}
            onChange={e => setSplitAmounts(prev => ({
              ...prev,
              [m.user_id]: e.target.value
            }))}
            style={{width: '80px', textAlign: 'right'}}
          />
        ) : (
          <span className="split-amount">
            {formatCurrency(splitAmounts[m.user_id] || 0, group.currency)}
          </span>
        )}
      </div>
    ))}
  </div>
</div>
              
              <button type="submit" className="bgd-submit-btn" disabled={addingExpense}>
                {addingExpense ? 'Adding...' : 'Add Expense'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ================= SETTLE UP MODAL ================= */}
      {showSettlement && (
        <div className="bgd-modal-overlay" onClick={() => setShowSettlement(false)}>
          <div className="bgd-modal small" onClick={e => e.stopPropagation()}>
            <div className="bgd-modal-header">
              <h2>Record Settlement</h2>
              <button className="bgd-modal-close" onClick={() => setShowSettlement(false)}>×</button>
            </div>
            <form onSubmit={handleAddSettlement} className="bgd-modal-form">
              <p className="bgd-settlement-note">I paid someone in the group</p>
              
              <div className="bgd-form-row">
                <label>Paid To *</label>
                <select value={settlementTo} onChange={e => setSettlementTo(e.target.value)} required>
                  <option value="">-- Select member --</option>
                  {group.members?.filter(m => m.user_id !== user?.id).map(m => (
                    <option key={m.user_id} value={m.user_id}>{m.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="bgd-form-row">
                <label>Amount ({group.currency}) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={settlementAmount}
                  onChange={e => setSettlementAmount(e.target.value)}
                  required
                />
              </div>

              <div className="bgd-form-row">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={settlementDate}
                  onChange={e => setSettlementDate(e.target.value)}
                />
              </div>
              
              <div className="bgd-form-row">
                <label>Note (optional)</label>
                <input
                  type="text"
                  value={settlementNote}
                  onChange={e => setSettlementNote(e.target.value)}
                  placeholder="e.g., Bank transfer"
                />
              </div>
              
              <button type="submit" className="bgd-submit-btn" disabled={addingSettlement}>
                {addingSettlement ? 'Recording...' : 'Record Payment'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ================= EXPENSE DETAIL MODAL ================= */}
      {selectedExpense && (
        <div className="bgd-modal-overlay" onClick={() => setSelectedExpense(null)}>
          <div className="bgd-modal large" onClick={e => e.stopPropagation()}>
            <div className="bgd-modal-header">
              <h2>Expense Details</h2>
              <button className="bgd-modal-close" onClick={() => setSelectedExpense(null)}>×</button>
            </div>
            
            {loadingExpenseDetail ? (
              <div className="bgd-mini-loading">Loading...</div>
            ) : (
              <div className="bgd-expense-detail">
                <div className="bgd-expense-detail-header">
                  <span className="detail-icon">{selectedExpense.category_icon || '📄'}</span>
                  <div className="detail-title">
                    <h3>{selectedExpense.description}</h3>
                    <span className="detail-category">{selectedExpense.category_name || 'Uncategorized'}</span>
                  </div>
                  <span className="detail-total">{formatCurrency(selectedExpense.amount, group.currency)}</span>
                </div>

                <div className="bgd-expense-detail-section">
                  <h4>📅 Date</h4>
                  <p>{formatDate(selectedExpense.expense_date)}</p>
                </div>

                <div className="bgd-expense-detail-section">
                  <h4>💳 Paid By</h4>
                  {selectedExpense.payers?.length > 0 ? (
                    <div className="detail-list">
                      {selectedExpense.payers.map((p, i) => (
                        <div key={i} className="detail-row">
                          <span>{p.name}</span>
                          <span className="detail-amount">{formatCurrency(p.amount, group.currency)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>{selectedExpense.paid_by_name || 'Unknown'}: {formatCurrency(selectedExpense.amount, group.currency)}</p>
                  )}
                </div>

                <div className="bgd-expense-detail-section">
                  <h4>📊 Split Between</h4>
                  <div className="detail-list">
                    {selectedExpense.splits?.map((s, i) => (
                      <div key={i} className="detail-row">
                        <span>
                          {s.name}
                          <span className="shares-badge">({(s.split_percentage || 0).toFixed(1)}%)</span>
                        </span>
                        <span className="detail-amount">{formatCurrency(s.amount, group.currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bgd-expense-detail-section">
                  <h4>📈 Net Effect</h4>
                  <div className="detail-list">
                    {selectedExpense.splits?.map((s, i) => {
                      let paid = 0;
                      if (selectedExpense.payers?.length > 0) {
                        const payer = selectedExpense.payers.find(p => p.user_id === s.user_id);
                        paid = payer?.amount || 0;
                      } else if (selectedExpense.paid_by === s.user_id) {
                        paid = selectedExpense.amount;
                      }
                      const net = paid - s.amount;
                      return (
                        <div key={i} className="detail-row">
                          <span>{s.name}</span>
                          <span className={`detail-amount ${net >= 0 ? 'positive' : 'negative'}`}>
                            {net >= 0 ? '+' : ''}{formatCurrency(net, group.currency)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Receipt Section */}
                <div className="bgd-expense-detail-section">
                  <h4>🧾 Receipt</h4>
                  {loadingReceipt ? (
                    <div className="bgd-mini-loading">Loading...</div>
                  ) : receiptInfo?.has_receipt ? (
                    <div className="receipt-section">
                      <div className="receipt-info">
                        <span className="receipt-filename">{receiptInfo.receipt.original_filename}</span>
                        <span className="receipt-size">
                          ({(receiptInfo.receipt.file_size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <div className="receipt-actions">
                        <button 
                          className="bgd-action-btn small"
                          onClick={() => handleDownloadReceipt(selectedExpense.id, receiptInfo.receipt.original_filename)}
                        >
                          📥 Download
                        </button>
                        {selectedExpense?.created_by === user?.id && (
                          <button 
                            className="bgd-action-btn small danger"
                            onClick={() => handleDeleteReceipt(selectedExpense.id)}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="receipt-section">
                      {user?.is_premium ? (
                        selectedExpense?.created_by === user?.id ? (
                          <div className="receipt-upload">
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={handleReceiptFileChange}
                              id="biz-receipt-file-input"
                              className="file-input"
                            />
                            <label htmlFor="biz-receipt-file-input" className="file-label">
                              {receiptFile ? receiptFile.name : "Choose file (JPG, PNG, PDF - Max 5MB)"}
                            </label>
                            {receiptFile && (
                              <button
                                className="bgd-action-btn small"
                                onClick={() => handleUploadReceipt(selectedExpense.id)}
                                disabled={uploadingReceipt}
                              >
                                {uploadingReceipt ? "Uploading..." : "📤 Upload"}
                              </button>
                            )}
                          </div>
                        ) : (
                          <p className="no-receipt-text">No receipt attached</p>
                        )
                      ) : (
                        <p className="premium-required-text">
                          ⭐ Premium feature - Upgrade to attach receipts
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Delete Button - Only for owner or admin */}
                {(selectedExpense?.created_by === user?.id || isAdmin) && (
                  <div className="bgd-expense-detail-section">
                    <button 
                      className="bgd-action-btn danger full-width"
                      onClick={() => handleDeleteExpense(selectedExpense.id)}
                    >
                      🗑️ Delete Expense
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="bgd-modal-actions">
              <button onClick={() => setSelectedExpense(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ================= ADD CATEGORY MODAL ================= */}
      {showAddCategory && (
        <div className="bgd-modal-overlay" onClick={() => setShowAddCategory(false)}>
          <div className="bgd-modal small" onClick={e => e.stopPropagation()}>
            <div className="bgd-modal-header">
              <h2>Add Custom Category</h2>
              <button className="bgd-modal-close" onClick={() => setShowAddCategory(false)}>×</button>
            </div>
            <form onSubmit={handleAddCategory} className="bgd-modal-form">
              <div className="bgd-form-row">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Project Supplies"
                  required
                />
              </div>
              <div className="bgd-form-row">
                <label>Icon</label>
                <input
                  type="text"
                  value={newCategoryIcon}
                  onChange={e => setNewCategoryIcon(e.target.value)}
                  maxLength={2}
                  style={{width: '60px', textAlign: 'center', fontSize: '18px'}}
                />
              </div>
              <button type="submit" className="bgd-submit-btn" disabled={addingCategory}>
                {addingCategory ? 'Adding...' : 'Add Category'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ================= ADD INCOME MODAL ================= */}
      {showAddIncome && (
        <div className="bgd-modal-overlay" onClick={() => setShowAddIncome(false)}>
          <div className="bgd-modal" onClick={e => e.stopPropagation()}>
            <div className="bgd-modal-header">
              <h2>Add Income</h2>
              <button className="bgd-modal-close" onClick={() => setShowAddIncome(false)}>×</button>
            </div>
            <form onSubmit={handleAddIncome} className="bgd-modal-form">
              {incomeError && <div className="bgd-error">{incomeError}</div>}
              
              {/* Amount */}
              <div className="bgd-form-row">
                <label>Amount ({group.currency}) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={incomeAmount}
                  onChange={e => setIncomeAmount(e.target.value)}
                  placeholder="Enter amount (negative to reverse)"
                  required
                />
                <small className="form-hint">Use negative amount to reverse a previous entry</small>
              </div>
              
              {/* Description */}
              <div className="bgd-form-row">
                <label>Description *</label>
                <input
                  type="text"
                  value={incomeDescription}
                  onChange={e => setIncomeDescription(e.target.value)}
                  placeholder="e.g., Q1 Revenue Distribution"
                  maxLength={255}
                  required
                />
              </div>

              {/* Source Type */}
              <div className="bgd-form-row">
                <label>Source Type</label>
                <select
                  value={incomeSourceType}
                  onChange={e => setIncomeSourceType(e.target.value)}
                >
                  <option value="">-- Select source type --</option>
                  {INCOME_SOURCE_TYPES.map(st => (
                    <option key={st.value} value={st.value}>{st.label}</option>
                  ))}
                </select>
              </div>

              {/* Source Name */}
              <div className="bgd-form-row">
                <label>Source Name</label>
                <input
                  type="text"
                  value={incomeSourceName}
                  onChange={e => setIncomeSourceName(e.target.value)}
                  placeholder="e.g., ABC Corporation"
                  maxLength={100}
                />
              </div>

              {/* Date */}
              <div className="bgd-form-row">
                <label>Income Date</label>
                <input
                  type="date"
                  value={incomeDate}
                  onChange={e => setIncomeDate(e.target.value)}
                />
              </div>

              {/* Split Type */}
              <div className="bgd-form-row">
                <label>Distribution Method</label>
                <select value={incomeSplitType} onChange={e => setIncomeSplitType(e.target.value)}>
                  <option value="equal">Equal Split</option>
                  <option value="percentage">By Percentage</option>
                  <option value="shares">By Shares</option>
                </select>
              </div>

              {/* Distribution Preview */}
              <div className="bgd-form-row">
                <label>Distribution Preview</label>
                <div className="bgd-split-preview">
                  {group.members?.map(m => (
                    <div key={m.user_id} className="split-preview-row">
                      <span className="split-member">{m.name}</span>
                      {incomeSplitType === 'shares' && (
                        <input
                          type="number"
                          min="0"
                          value={incomeMemberShares[m.user_id] || 1}
                          onChange={e => setIncomeMemberShares(prev => ({
                            ...prev,
                            [m.user_id]: parseInt(e.target.value) || 0
                          }))}
                          className="split-shares-input"
                          style={{width: '50px', marginRight: '10px'}}
                        />
                      )}
                      <span className="split-amount">
                        {formatCurrency(incomeSplitAmounts[m.user_id] || 0, group.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="bgd-form-row">
                <label>Notes</label>
                <textarea
                  value={incomeNotes}
                  onChange={e => setIncomeNotes(e.target.value)}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
              
              <button type="submit" className="bgd-submit-btn" disabled={addingIncome}>
                {addingIncome ? 'Adding...' : 'Add Income'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ================= SETTLE FROM INCOME MODAL (Admin Only) ================= */}
      {showSettleFromIncome && (
        <div className="bgd-modal-overlay" onClick={() => setShowSettleFromIncome(false)}>
          <div className="bgd-modal small" onClick={e => e.stopPropagation()}>
            <div className="bgd-modal-header">
              <h2>💸 Distribute Income</h2>
              <button className="bgd-modal-close" onClick={() => setShowSettleFromIncome(false)}>×</button>
            </div>
            <form onSubmit={handleSettleFromIncome} className="bgd-modal-form">
              <div className="bgd-info-box">
                <strong>Group Income Balance:</strong>{' '}
                <span className={groupIncomeBalance < 0 ? 'negative' : ''}>
                  {formatCurrency(Math.abs(groupIncomeBalance), group.currency)} available to distribute
                </span>
              </div>

              {/* To Member */}
              <div className="bgd-form-row">
                <label>Pay To *</label>
                <select
                  value={settleFromIncomeTo}
                  onChange={e => setSettleFromIncomeTo(e.target.value)}
                  required
                >
                  <option value="">-- Select member --</option>
                  {group.members?.map(m => (
                    <option key={m.user_id} value={m.user_id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div className="bgd-form-row">
                <label>Amount ({group.currency}) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={settleFromIncomeAmount}
                  onChange={e => setSettleFromIncomeAmount(e.target.value)}
                  required
                />
              </div>

              {/* Date */}
              <div className="bgd-form-row">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={settleFromIncomeDate}
                  onChange={e => setSettleFromIncomeDate(e.target.value)}
                />
              </div>

              {/* Note */}
              <div className="bgd-form-row">
                <label>Note</label>
                <input
                  type="text"
                  value={settleFromIncomeNote}
                  onChange={e => setSettleFromIncomeNote(e.target.value)}
                  placeholder="e.g., Q1 dividend distribution"
                />
              </div>
              
              <button type="submit" className="bgd-submit-btn" disabled={settlingFromIncome}>
                {settlingFromIncome ? 'Processing...' : 'Record Payment'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ================= INCOME DETAIL MODAL ================= */}
      {selectedIncome && (
        <div className="bgd-modal-overlay" onClick={() => setSelectedIncome(null)}>
          <div className="bgd-modal large" onClick={e => e.stopPropagation()}>
            <div className="bgd-modal-header">
              <h2>Income Details</h2>
              <button className="bgd-modal-close" onClick={() => setSelectedIncome(null)}>×</button>
            </div>
            
            {loadingIncomeDetail ? (
              <div className="bgd-mini-loading">Loading...</div>
            ) : (
              <div className="bgd-expense-detail">
                {/* Income Info */}
                <div className="bgd-expense-detail-header">
                  <span className="detail-icon">
                    {INCOME_SOURCE_TYPES.find(t => t.value === selectedIncome.source_type)?.icon || '💰'}
                  </span>
                  <div className="detail-title">
                    <h3>{selectedIncome.description}</h3>
                    {selectedIncome.source_name && (
                      <span className="detail-category">{selectedIncome.source_name}</span>
                    )}
                  </div>
                  <span className={`detail-total ${selectedIncome.amount >= 0 ? 'positive' : 'negative'}`}>
                    {selectedIncome.amount >= 0 ? '+' : ''}{formatCurrency(selectedIncome.amount, selectedIncome.currency)}
                    {selectedIncome.amount < 0 && <span className="reversal-badge">REVERSAL</span>}
                  </span>
                </div>

                <div className="bgd-expense-detail-section">
                  <h4>📅 Date</h4>
                  <p>{formatDate(selectedIncome.income_date)}</p>
                </div>

                {selectedIncome.source_type && (
                  <div className="bgd-expense-detail-section">
                    <h4>📋 Source Type</h4>
                    <p>{INCOME_SOURCE_TYPES.find(t => t.value === selectedIncome.source_type)?.label || selectedIncome.source_type}</p>
                  </div>
                )}

                <div className="bgd-expense-detail-section">
                  <h4>👤 Added By</h4>
                  <p>{selectedIncome.created_by_name}</p>
                </div>

                {selectedIncome.notes && (
                  <div className="bgd-expense-detail-section">
                    <h4>📝 Notes</h4>
                    <p>{selectedIncome.notes}</p>
                  </div>
                )}

                {/* Distribution */}
                <div className="bgd-expense-detail-section">
                  <h4>📊 Distribution ({selectedIncome.split_type})</h4>
                  <div className="detail-list">
                    {selectedIncome.splits?.map((s, i) => (
                      <div key={i} className="detail-row">
                        <span>{s.name}</span>
                        <span className={`detail-amount ${selectedIncome.amount >= 0 ? 'positive' : 'negative'}`}>
                          {selectedIncome.amount >= 0 ? '+' : ''}{formatCurrency(s.amount, selectedIncome.currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Receipt Section */}
                <div className="bgd-expense-detail-section">
                  <h4>🧾 Receipt</h4>
                  {loadingIncomeReceipt ? (
                    <div className="bgd-mini-loading">Loading receipt info...</div>
                  ) : incomeReceiptInfo?.has_receipt ? (
                    <div className="receipt-section">
                      <div className="receipt-info">
                        <span className="receipt-filename">{incomeReceiptInfo.receipt?.original_filename}</span>
                        <span className="receipt-size">
                          ({Math.round((incomeReceiptInfo.receipt?.file_size || 0) / 1024)} KB)
                        </span>
                      </div>
                      <div className="receipt-actions">
                        <button
                          className="bgd-action-btn small"
                          onClick={() => handleDownloadIncomeReceipt(
                            selectedIncome.id,
                            incomeReceiptInfo.receipt?.original_filename
                          )}
                        >
                          📥 Download
                        </button>
                        {selectedIncome.created_by === user?.id && (
                          <button
                            className="bgd-action-btn small danger"
                            onClick={() => handleDeleteIncomeReceipt(selectedIncome.id)}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ) : selectedIncome.created_by === user?.id ? (
                    <div className="receipt-upload">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleIncomeReceiptFileChange}
                        id="income-receipt-file-input"
                        className="file-input"
                      />
                      <label htmlFor="income-receipt-file-input" className="file-label">
                        {incomeReceiptFile ? incomeReceiptFile.name : "Choose file (JPG, PNG, PDF - Max 5MB)"}
                      </label>
                      {incomeReceiptFile && (
                        <button
                          className="bgd-action-btn small"
                          onClick={() => handleUploadIncomeReceipt(selectedIncome.id)}
                          disabled={uploadingIncomeReceipt}
                        >
                          {uploadingIncomeReceipt ? 'Uploading...' : '📤 Upload Receipt'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="no-receipt-text">No receipt attached</p>
                  )}
                </div>
              </div>
            )}

            <div className="bgd-modal-actions">
              <button onClick={() => setSelectedIncome(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessGroupDetail;

